import { ShapefileTag, ZoneShape } from "../../presentation/components/pages/ui-utils/zoneFilterTypes";
import ConfigurationRepository from "../data/ConfigurationRepository";
import OrganizationRepository from "../data/OrganizationRepository";
import PermissionRepository from "../data/PermissionRepository";
import PermissionTypeRepository from "../data/PermissionTypeRepository";
import PostgisRepository from "../data/PostgisRepository";
import RoleRepository from "../data/RoleRepository";
import UserRepository from "../data/UserRepository";
import { ClassroomSizeConfig, FeederPatternConfig, FeederPatternImportResult, SchoolDirectoryBatchResult, SchoolDirectoryEntry, SeatLimitConfig, SiteConfigs } from "../entities/Configs";
import DocumentTemplate from "../entities/DocumentTemplate";
import Organization from "../entities/Organization";
import Permission from "../entities/Permission";
import PermissionType from "../entities/PermissionType";
import Role from "../entities/Role";
import User from "../entities/User";

export default class AdministratorInteractor {
  private organizationRepository: OrganizationRepository;
  private permissionRepository: PermissionRepository;
  private permissionTypeRepository: PermissionTypeRepository;
  private roleRepository: RoleRepository;
  private userRepository: UserRepository;
  private configurationRepository: ConfigurationRepository;
  private postgisRepository: PostgisRepository;

  constructor(
    organizationRepository: OrganizationRepository,
    permissionRepository: PermissionRepository,
    permissionTypeRepository: PermissionTypeRepository,
    roleRepository: RoleRepository,
    userRepository: UserRepository,
    configurationRepository: ConfigurationRepository,
    postgisRepository: PostgisRepository
  ) {
    this.organizationRepository = organizationRepository;
    this.permissionRepository = permissionRepository;
    this.permissionTypeRepository = permissionTypeRepository;
    this.roleRepository = roleRepository;
    this.userRepository = userRepository;
    this.configurationRepository = configurationRepository;
    this.postgisRepository = postgisRepository;
  }

  //only for Paisley Admin users
  public async getOrganizations(user: User): Promise<Organization[]> {
    return this.organizationRepository.getOrganizations(user);
  }

  public async getOrganization(orgId: string): Promise<Organization> {
    return this.organizationRepository.getOrganization(orgId);
  }

  public async deleteOrganization(orgId: string): Promise<Boolean> {
    return this.organizationRepository.deleteOrganization(orgId);
  }

  public async saveOrganizations(org: Organization): Promise<Organization> {
    return this.organizationRepository.saveOrganizations(org);
  }

  public async getOrganizationByURl(orgUrl: string): Promise<Organization> {
    return this.organizationRepository.getOrganizationByURl(orgUrl);
  }

  public async saveUser(user: User, userObject: User): Promise<User> {
    //TODO: Generate a Signatory on backend everytime we regsiter a user
    return this.userRepository.saveUser(user, userObject);
  }

  public async cognitoCreateUser(
    userid: String,
    organizationId: String,
    email: String
  ): Promise<Boolean> {
    return this.userRepository.cognitoCreateUser(
      userid,
      organizationId,
      email
    );
  }

  public async cognitoResendUser(
    userid: String,
    organizationId: String,
    email: String
  ): Promise<Boolean> {
    return this.userRepository.cognitoResendUser(
      userid,
      organizationId,
      email
    );
  }

  public async cognitoCheckUserExist(email: String): Promise<Boolean> {
    return this.userRepository.cognitoCheckUserExist(email);
  }

  public async deleteUser(user: User, userObject: User): Promise<Boolean> {
    return this.userRepository.deleteUser(user, userObject);
  }

  public async getUserByShortId(shortId: string): Promise<User | null> {
    return this.userRepository.getUserByShortId(shortId);
  }

  public async getUserByEmail(organizationId: string, email: string): Promise<User | null> {
    return this.userRepository.getUserByEmail(organizationId, email);
  }

  public async getUsers(user: User): Promise<User[]> {
    return this.userRepository.getUsers(user);
  }

  public async registerUser(
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    orgName: string,
    phoneNumber?: string,
    smsConsent?: boolean
  ): Promise<User | undefined> {
    return this.userRepository.registerUser(
      firstName,
      lastName,
      email,
      password,
      orgName,
      phoneNumber,
      smsConsent
    );
  }

  public async getRoles(user: User): Promise<Role[]> {
    return this.roleRepository.getRoles(user);
  }

  public async getPermissionTypes(user: User): Promise<PermissionType[]> {
    return this.permissionTypeRepository.getPermissionTypes(user);
  }

  public async savePermission(
    user: User,
    permission: Permission
  ): Promise<Permission> {
    return this.permissionRepository.savePermission(user, permission);
  }

  public async getPermissions(
    user: User,
    documentTemplate: DocumentTemplate
  ): Promise<Permission[]> {
    return this.permissionRepository.getPermissions(user, documentTemplate);
  }

  public async getSiteConfigs(
    orgId: string,
    page: string
  ): Promise<SiteConfigs[]> {
    return this.configurationRepository.getSiteConfigs(orgId, page);
  }

  public async saveSiteConfigs(
    orgId: string,
    page: string,
    siteConfigs: SiteConfigs
  ): Promise<SiteConfigs> {
    return this.configurationRepository.saveSiteConfigs(
      orgId,
      page,
      siteConfigs
    );
  }

  public async saveImage(
    orgId: string,
    fileName: string,
    file: File
  ): Promise<Boolean> {
    return this.configurationRepository.saveImage(orgId, fileName, file);
  }

  public async getImage(orgId: string, fileName: string): Promise<File> {
    return this.configurationRepository.getImage(orgId, fileName);
  }

  public async saveShapefiles(organizationId: string, shapes: ZoneShape[]): Promise<ZoneShape[]> {
    return this.postgisRepository.saveShapefiles(organizationId, shapes);
  }
  public async updateShapefileName(organizationId: string, shapefileId: string, name: string): Promise<any> {
    return this.postgisRepository.updateShapefileName(organizationId, shapefileId, name);
  }
  public async updateShapefileTags(organizationId: string, shapefileId: string, tags: ShapefileTag[]): Promise<any> {
    return this.postgisRepository.updateShapefileTags(organizationId, shapefileId, tags);
  }
  public async updateShapefileDescription(organizationId: string, shapefileId: string, description: string): Promise<any> {
    return this.postgisRepository.updateShapefileDescription(organizationId, shapefileId, description);
  }
  public async loadShapefiles(organizationId: string): Promise<ZoneShape[]> {
    return this.postgisRepository.loadShapefiles(organizationId);
  }
  public async loadShapefilesList(organizationId: string): Promise<ZoneShape[]> {
    return this.postgisRepository.loadShapefilesList(organizationId);
  }
  public async loadShapefileGeometry(organizationId: string, shapefileId: string): Promise<ZoneShape> {
    return this.postgisRepository.loadShapefileGeometry(organizationId, shapefileId);
  }
  public async deleteShapefile(organizationId: string, shapefileId: string): Promise<Boolean> {
    return this.postgisRepository.deleteShapefile(organizationId, shapefileId);
  }

  public async saveSeatLimitConfig(seatLimitConfig: SeatLimitConfig): Promise<SeatLimitConfig> {
    return this.configurationRepository.saveSeatLimitConfig(seatLimitConfig);
  }
  public async getSeatLimitConfigs(organizationId: string): Promise<SeatLimitConfig[]> {
    return this.configurationRepository.getSeatLimitConfigs(organizationId);
  }
  public async saveClassroomSizeConfig(classroomSizeConfig: ClassroomSizeConfig): Promise<ClassroomSizeConfig> {
    return this.configurationRepository.saveClassroomSizeConfig(classroomSizeConfig);
  }
  public async getClassroomSizeConfigs(organizationId: string): Promise<ClassroomSizeConfig[]> {
    return this.configurationRepository.getClassroomSizeConfigs(organizationId);
  }

  // ── Feeder Pattern ─────────────────────────────────────

  public async saveFeederPatternConfig(feederPattern: FeederPatternConfig): Promise<FeederPatternConfig> {
    return this.configurationRepository.saveFeederPatternConfig(feederPattern);
  }
  public async getFeederPatternConfigs(organizationId: string): Promise<FeederPatternConfig[]> {
    return this.configurationRepository.getFeederPatternConfigs(organizationId);
  }
  public async deleteFeederPatternConfig(organizationId: string, feederPatternId: string): Promise<void> {
    return this.configurationRepository.deleteFeederPatternConfig(organizationId, feederPatternId);
  }
  public async importFeederPatternConfigs(organizationId: string, configs: FeederPatternConfig[]): Promise<FeederPatternImportResult> {
    return this.configurationRepository.importFeederPatternConfigs(organizationId, configs);
  }

  // ── School Directory ─────────────────────────────────────

  public async getSchoolDirectory(organizationId: string): Promise<SchoolDirectoryEntry[]> {
    return this.configurationRepository.getSchoolDirectory(organizationId);
  }
  public async saveSchoolDirectoryEntry(organizationId: string, entry: SchoolDirectoryEntry): Promise<SchoolDirectoryEntry> {
    return this.configurationRepository.saveSchoolDirectoryEntry(organizationId, entry);
  }
  public async saveSchoolDirectoryBatch(organizationId: string, entries: SchoolDirectoryEntry[]): Promise<SchoolDirectoryBatchResult> {
    return this.configurationRepository.saveSchoolDirectoryBatch(organizationId, entries);
  }
  public async deleteSchoolDirectoryEntry(organizationId: string, schoolId: string): Promise<void> {
    return this.configurationRepository.deleteSchoolDirectoryEntry(organizationId, schoolId);
  }
}
